import { SetStateAction, useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import logoMobileSrc from "@/src/logo_no_icon.svg";
import logoSrc from "@/src/logo.svg";
import { LogOutIcon, MenuIcon } from "lucide-react";
import { userFullName, userInitials, useUserStore } from "./stores/userStore";
import { Separator } from "@/src/components/ui/separator";
import { logout } from "./app/Login/logout";
import { useSpring, animated, config } from "@react-spring/web";
import { useMediaQuery } from "react-responsive";
import { cn } from "./lib/utils";
import { MENU, MenuItem, menuItemVisible, visibleMenuSections } from "./Navigation";

export default function MainLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const isSmall = useMediaQuery({ query: "(max-width: 639px)" });

  return (
    <div className="w-screen h-screen grid grid-cols-1 grid-rows-[60px_auto] sm:grid-rows-1 sm:grid-cols-[238px_auto]">
      {isSmall ? <MainMenuMobile menuOpen={menuOpen} setMenuOpen={setMenuOpen} /> : <MainMenuDesktop />}
      <div className="overflow-hidden relative">
        {isSmall && (
          <div
            data-menu-open={menuOpen}
            onClickCapture={(ev) => {
              ev.preventDefault();
              setMenuOpen(false);
            }}
            className={cn("absolute top-0 left-0 w-full h-full bg-slate-900/60 transition-opacity z-40", {
              "opacity-0 max-h-0": !menuOpen,
            })}
          ></div>
        )}
        <Outlet />
      </div>
    </div>
  );
}

function MainMenuMobile(props: { menuOpen: boolean; setMenuOpen: (value: SetStateAction<boolean>) => void }) {
  const { menuOpen, setMenuOpen } = props;
  const user = useUserStore().user!;
  const visibleItems = MENU.filter((item) => menuItemVisible(item, user));
  const [dropdownProps, api] = useSpring(() => ({
    height: 0,
    config: config.stiff,
  }));

  useEffect(() => {
    const menuHeight =
      visibleItems.length * 40 +
      16 + // container padding
      40 + // logout button
      visibleItems.length * 4 + // gap between items
      5; // separator
    if (menuOpen) {
      api.start({
        height: menuHeight,
      });
    } else {
      api.start({
        height: 0,
      });
    }
  }, [api, menuOpen, visibleItems.length]);

  return (
    <div className="py-2 px-4 relative flex justify-between items-center border-b border-b-blue-10 bg-white">
      <img src={logoMobileSrc} alt="Helm"></img>
      <button onClick={() => setMenuOpen((open) => !open)} className="flex items-center gap-4">
        <MenuIcon />
        <MenuAvatar highlighted={menuOpen} />
      </button>
      <animated.nav
        className="left-0 z-50 overflow-hidden absolute top-14 mt-1 w-full bg-white "
        style={{
          height: dropdownProps.height.to((value) => `${value}px`),
        }}
      >
        <div className="py-2 px-4 flex flex-col gap-1">
          {visibleItems.map((item) => (
            <MenuNavItem item={item} key={item.label} />
          ))}
          <Separator />
          <LogOutButton />
        </div>
      </animated.nav>
    </div>
  );
}

function MenuAvatar(props: { highlighted?: boolean }) {
  const user = useUserStore().user!;
  const initials = userInitials(user);

  const className = cn("w-10 h-10 bg-blue-100 text-slate-900 rounded-full flex shrink-0 justify-center items-center", {
    "bg-blue-300": props.highlighted,
  });

  return <div className={className}>{initials}</div>;
}

function MainMenuDesktop() {
  const user = useUserStore().user!;
  return (
    <div className="p-2 pb-6 flex flex-col items-start gap-4 bg-white border-r border-r-blue-100">
      <img style={{ height: 40, margin: 12 }} src={logoSrc} alt="Helm" />
      <div className="flex flex-row justify-start gap-4 items-center w-full px-1">
        <MenuAvatar />
        <span className="truncate">{userFullName(user)}</span>
      </div>
      <Separator />
      <div className="flex flex-col gap-1 w-full overflow-y-auto">
        {visibleMenuSections(user).map((section) => (
          <div key={section.label} className="flex flex-col gap-1 w-full">
            <div className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400 select-none">
              {section.label}
            </div>
            {section.items.map((item) => (
              <MenuNavItem item={item} key={item.label} />
            ))}
          </div>
        ))}
      </div>
      <Separator className="mt-auto" />
      <LogOutButton />
    </div>
  );
}

function LogOutButton() {
  return (
    <button onClick={logout} className="py-2 flex gap-3 items-center sm:gap-4 sm:px-4">
      <LogOutIcon />
      Log out
    </button>
  );
}

function MenuNavItem({ item }: { item: MenuItem }) {
  return (
    <NavLink
      className={({ isActive }) =>
        cn("py-2 flex gap-3 items-center rounded-lg sm:gap-4 sm:px-4", {
          "bg-blue-50": isActive,
        })
      }
      to={item.href}
      end={item.href === "/"}
      key={item.label}
    >
      {item.icon}
      {item.label}
    </NavLink>
  );
}
